import { MaxWidthWrapper } from "@/components/maxWidthWrapper";
import { ScrollingEye } from "./ScrollingEye";
import { ScrollingNose } from "./ScrollingNose";
import {
  BadgeCheckIcon,
  BookCheckIcon,
  GlobeLockIcon,
  LockIcon,
  ScanFaceIcon,
  type LucideIcon,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import type { ReactNode } from "react";

export const AboutSection = () => (
  <MaxWidthWrapper className="mb-10">
    <Card className="px-12">
      <div className="relative flex flex-col items-center lg:flex-row">
        <ScrollingEye />
        <div>
          <h1 className="py-4 text-xl font-bold text-foreground sm:text-2xl md:text-4xl">
            Why Flash?
          </h1>
          <h3 className="mb-8 text-xl font-bold text-primary sm:text-left lg:text-lg dark:text-sky-300">
            Unmatched Speed, Security, and Scale
          </h3>
          <div className="space-y-8">
            <BulletPoint icon={ScanFaceIcon}>
              Flash Biometrics sets the standard for facial recognition with industry-leading speed
              and precision. Designed to recognize multiple faces simultaneously, it excels at
              handling massive data sets of hundreds of thousands of identities without compromising
              performance.
            </BulletPoint>
            <BulletPoint icon={LockIcon}>
              Flash's privacy-first architecture ensures that personal data remains secure and
              protected at every stage. Whether it's verifying access at large-scale events or
              managing high-volume systems, Flash Biometrics delivers unparalleled efficiency,
              accuracy, and peace of mind.
            </BulletPoint>
          </div>
        </div>
      </div>
      <div className="relative mb-8 flex flex-col items-center lg:flex-row">
        <div>
          <h3 className="mb-8 text-left font-bold text-primary md:text-xl lg:text-lg dark:text-sky-300">
            Privacy-First Technology Built for Trust
          </h3>
          <div className="space-y-8">
            <BulletPoint icon={BookCheckIcon}>
              At Flash, privacy isn't just a feature—it's the foundation of everything we do. Our
              system is designed to meet the highest global privacy standards, including compliance
              with laws such as the General Data Protection Regulation (GDPR) in Europe, the
              California Consumer Privacy Act (CCPA), and other international data protection
              frameworks.
            </BulletPoint>
            <BulletPoint icon={GlobeLockIcon}>
              We follow strict protocols to ensure that user data is encrypted during both
              transmission and storage, protecting sensitive information at every stage. We use a
              privacy-by-design approach, meaning that data minimization, transparency, and user
              control are at the core of our solution. We never store biometric data in its raw
              form; instead, we use encrypted representations that cannot be reverse-engineered.
              Flash only uses data for verification purposes, and we do not sell or share user data
              with third parties.
            </BulletPoint>
            <BulletPoint icon={BadgeCheckIcon}>
              Our commitment to privacy helps organizations build trust with their users while
              enhancing security and efficiency. By adhering to the highest industry standards and
              regulations, we ensure that your organization can deliver a secure, seamless
              experience without compromising personal privacy.
            </BulletPoint>
          </div>
        </div>
        <ScrollingNose />
      </div>
    </Card>
  </MaxWidthWrapper>
);

function BulletPoint({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex justify-around text-card-foreground">
      <div className="not-sr-only mr-3 size-14 p-4 text-primary max-sm:hidden dark:text-sky-300">
        <Icon />
      </div>
      <div className="font-serif">{children}</div>
    </div>
  );
}
